import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import { Send, Loader2, Trash2 } from 'lucide-react';

const DEFAULT_TITLE = 'AI Agent';
const DEFAULT_SUBTITLE = "Ask questions about your organization's risk data";

// The agent's chat surface — message list, markdown rendering, input, send, clear, error and the
// per-message meta line. `AgentPage` composes it for `/agent` (adding the page chrome and the
// voice pipeline); the AI Intelligence "Ask" drawer composes it in `compact` layout.
//
// Props:
//   context            optional request context ({ scope, assetId?, tab? }) passed through
//                      untouched on the wire; omitted from the body when undefined
//   compact            tighter paddings, no page-level heading, input pinned to the bottom
//   placeholder        textarea placeholder
//   suggestions        strings rendered as clickable chips while the conversation is empty
//   onSendRef          a ref object that receives `sendQuestion` (used by the voice pipeline)
//   onMessagesChange   called with the current messages array whenever it changes
//   extraError         an extra error string rendered exactly like the request error
//   renderInputActions slot (function) rendered in the input's button row, before Send;
//                      `children` is used when no function is given
const AgentChat = ({
  context,
  compact = false,
  placeholder = 'Ask a question...',
  suggestions = [],
  onSendRef,
  onMessagesChange,
  extraError,
  renderInputActions,
  children,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Mirror of messages for async callers (the voice pipeline) — history must be built from
  // the state as it was before the new user message is appended.
  const messagesRef = useRef(messages);
  const onMessagesChangeRef = useRef(onMessagesChange);
  useEffect(() => {
    onMessagesChangeRef.current = onMessagesChange;
  }, [onMessagesChange]);
  useEffect(() => {
    messagesRef.current = messages;
    onMessagesChangeRef.current?.(messages);
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Shared send path for typed and spoken questions. Returns the agent response (the voice
  // pipeline reads spokenAnswer from it); rethrows so the voice pipeline can surface errors.
  const sendQuestion = useCallback(async (text, { voice = false } = {}) => {
    const question = (text || '').trim();
    if (!question) return null;

    const history = messagesRef.current.map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setLoading(true);
    setError(null);

    const body = { question, history, voiceMode: voice };
    if (context !== undefined) body.context = context;

    try {
      const response = await api.post('/Agent/query', body);
      const assistantMessage = {
        role: 'assistant',
        content: response.data.answer,
        meta: {
          iterations: response.data.iterations,
          toolsUsed: response.data.toolsUsed,
          toolCallCount: response.data.toolCallCount,
          spokenAnswer: response.data.spokenAnswer,
        },
      };
      setMessages((prev) => [...prev, assistantMessage]);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    if (onSendRef) onSendRef.current = sendQuestion;
  }, [onSendRef, sendQuestion]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const question = input.trim();
    setInput('');
    try {
      await sendQuestion(question);
    } catch {
      // error state is already set inside sendQuestion
    }
  };

  const handleSuggestion = async (text) => {
    if (loading) return;
    try {
      await sendQuestion(text);
    } catch {
      // error state is already set inside sendQuestion
    }
  };

  const handleClear = () => {
    setMessages([]);
    setInput('');
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const px = compact ? 'px-4' : 'px-6';
  const mx = compact ? 'mx-4' : 'mx-6';
  const py = compact ? 'py-3' : 'py-4';
  const showSuggestions = messages.length === 0 && !loading && suggestions.length > 0;

  const clearButton = (
    <button
      onClick={handleClear}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-content-muted hover:text-content-primary border border-border rounded-lg hover:bg-theme-primary transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
      Clear
    </button>
  );

  return (
    <div
      className={
        compact
          ? 'agent-chat--compact flex flex-col flex-1 min-h-0 h-full'
          : 'bg-theme-secondary rounded-xl border border-border shadow-theme-lg flex flex-col flex-1 min-h-0'
      }
    >
      {/* Header */}
      {compact ? (
        messages.length > 0 && (
          <div className="flex items-center justify-end px-4 py-2 border-b border-border">{clearButton}</div>
        )
      ) : (
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h1 className="text-xl font-semibold text-content-primary">{DEFAULT_TITLE}</h1>
            <p className="text-sm text-content-secondary">{DEFAULT_SUBTITLE}</p>
          </div>
          {messages.length > 0 && clearButton}
        </div>
      )}

      {/* Messages */}
      <div className={`flex-1 overflow-y-auto ${px} ${py} space-y-4 min-h-0`}>
        {messages.length === 0 && !loading && !showSuggestions && (
          <div className="flex items-center justify-center h-full text-content-muted text-sm">
            Ask a question to get started
          </div>
        )}

        {showSuggestions && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <p className="text-content-muted text-sm">Ask a question to get started</p>
            <div className="flex flex-wrap justify-center gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleSuggestion(s)}
                  className="px-3 py-1.5 text-xs text-content-secondary hover:text-content-primary bg-theme-primary border border-border rounded-full hover:border-accent-cyan/40 transition-colors text-left"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-accent-cyan/10 border border-accent-cyan/20 text-content-primary'
                  : 'bg-theme-primary border border-border text-content-primary'
              }`}
            >
              {msg.role === 'assistant' ? (
                <>
                  <div className="prose prose-sm max-w-none text-content-primary prose-headings:text-content-primary prose-strong:text-content-primary prose-code:text-accent-cyan prose-code:bg-theme-secondary prose-code:px-1 prose-code:rounded prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-theme-secondary prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </div>
                  {msg.meta && (msg.meta.toolsUsed?.length > 0 || msg.meta.iterations) && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-border">
                      {msg.meta.iterations && (
                        <span className="text-xs text-content-muted px-2 py-0.5 bg-theme-secondary border border-border rounded">
                          {msg.meta.iterations} iteration{msg.meta.iterations !== 1 ? 's' : ''}
                        </span>
                      )}
                      {msg.meta.toolsUsed?.map((tool, j) => (
                        <span key={j} className="text-xs text-content-muted px-2 py-0.5 bg-theme-secondary border border-border rounded">
                          {tool}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-theme-primary border border-border rounded-xl px-4 py-3 flex items-center gap-2 text-content-muted text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Thinking...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className={`${mx} mb-2 p-3 bg-risk-critical-bg border border-risk-critical/30 rounded-lg`}>
          <p className="text-sm text-risk-critical">{error}</p>
        </div>
      )}
      {extraError && (
        <div className={`${mx} mb-2 p-3 bg-risk-critical-bg border border-risk-critical/30 rounded-lg`}>
          <p className="text-sm text-risk-critical">{extraError}</p>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className={`${px} ${py} border-t border-border${compact ? ' mt-auto' : ''}`}>
        <div className="flex items-end gap-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className="flex-1 px-4 py-2.5 bg-theme-primary border border-border rounded-lg text-content-primary placeholder-content-muted resize-none focus:outline-none focus:ring-2 focus:ring-accent-cyan focus:border-transparent text-sm"
            style={{ maxHeight: '120px', minHeight: '42px' }}
            disabled={loading}
          />
          {renderInputActions ? renderInputActions() : children}
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="flex items-center justify-center w-10 h-10 bg-gradient-primary text-white rounded-lg transition-opacity disabled:opacity-50 hover:opacity-90 shrink-0"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgentChat;
